import type { ClientId, EquipmentId, AreaId, WorkOrderId, InstrumentId, SafetyDeviceId, InstrumentCalibrationId, SafetyDeviceTestId } from "../shared/kernel/identifiers.js";

export type EquipmentType = 
  | 'PRESSURE_VESSEL' 
  | 'STORAGE_TANK' 
  | 'HEAT_EXCHANGER' 
  | 'BOILER' 
  | 'FURNACE' 
  | 'PIPING_SYSTEM' 
  | 'PIPING_LINE' 
  | 'OTHER';

export type EquipmentStatus = 'ACTIVE' | 'OUT_OF_SERVICE' | 'INACTIVE';
export type CriticalityLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface InServiceNormativeCodes {
  inServiceInspectionCode?: string | null;  // e.g. API 510, API 570, API 653
  designConstructionCode?: string | null;   // e.g. ASME Sec VIII Div 1, ASME B31.3, API 650
  recommendedPractice?: string | null;      // e.g. API RP 572, API RP 574, API RP 575
  regulatoryRequirement?: string | null;    // e.g. NR-13
}

export interface MeasurementValue {
  value: number | null;
  unit: string; // bar, MPa, psi, kgf/cm², °C, °F, mm, in
}

export interface MinimumThicknessRecord {
  value: number | null;
  unit: string;
  source: 'MANUAL' | 'FORMULA_ENGINE' | 'CALCULATED';
  responsible?: string | null;
  date?: string | null;
  calculationBasis?: string | null; // e.g. "ASME B31.3 Eq. 304.1.2", "ASME VIII Div 1 UG-27", "Folha de Dados do Fabricante"
}

export interface EquipmentDamageMechanism {
  id: string;
  equipmentId: string;
  damageMechanismId: string;
  name: string;
  status: 'SUSPECTED' | 'CONFIRMED' | 'INACTIVE';
  /** 'CORTEX_SUGGESTION' = legado (registros antigos); novas sugestões são 'AI_REVIEW_SUGGESTION'. */
  source: 'MANUAL' | 'AI_REVIEW_SUGGESTION' | 'CORTEX_SUGGESTION' | 'INSPECTION';
  identifiedAt: string;
  identifiedBy: string;
  confirmedBy?: string | null;
  confirmedAt?: string | null;
  notes?: string | null;
}

export type Nr13Applicability = 'APPLICABLE' | 'NOT_APPLICABLE' | 'UNCLASSIFIED';

export const STANDARD_DAMAGE_MECHANISMS = [
  { id: 'DM-CUI', code: 'API-571-3.22', name: 'Corrosão Sob Isolamento (CUI)' },
  { id: 'DM-ATM-CORR', code: 'API-571-3.11', name: 'Corrosão Atmosférica' },
  { id: 'DM-INT-CORR', code: 'API-571-3.27', name: 'Corrosão Interna Uniforme / Alvéolos' },
  { id: 'DM-EROSION', code: 'API-571-3.29', name: 'Erosão / Erosão-Corrosão' },
  { id: 'DM-SCC-CL', code: 'API-571-3.17', name: 'Corrosão Sob Tensão por Cloretos (Cl-SCC)' },
  { id: 'DM-FATIGUE-MECH', code: 'API-571-3.43', name: 'Fadiga Mecânica (Vibração em Linha)' },
  { id: 'DM-FATIGUE-THERM', code: 'API-571-3.61', name: 'Fadiga Térmica' },
  { id: 'DM-CREEP', code: 'API-571-3.23', name: 'Fluência (Creep)' },
  { id: 'DM-HIC', code: 'API-571-3.34', name: 'Fragilização por Hidrogênio / Empolamento (HIC)' },
  { id: 'DM-HTHA', code: 'API-571-3.35', name: 'Ataque por Hidrogênio em Alta Temperatura (HTHA)' },
  { id: 'DM-MIC', code: 'API-571-3.45', name: 'Corrosão Microbiológica (MIC)' }
] as const;

export interface PipingLine {
  id: string;
  lineNumber: string;
  service: string | null;
  fluid: string | null;
  fluidClass?: string | null;
  from: string | null;
  to: string | null;
  fromEquipmentId?: string | null;
  fromReference?: string | null;
  toEquipmentId?: string | null;
  toReference?: string | null;
  nominalDiameter: string | null;
  nps?: string | null;
  schedule: string | null;
  material: string | null;
  designPressure: number | string | MeasurementValue | null;
  designTemperature: number | string | MeasurementValue | null;
  operatingPressure: number | string | MeasurementValue | null;
  operatingTemperature: number | string | MeasurementValue | null;
  nominalThickness?: number | string | MeasurementValue | null;
  minimumRequiredThickness?: number | string | MinimumThicknessRecord | null;
  corrosionAllowance: number | string | MeasurementValue | null;
  fabricationStandard?: string | null;
  inServiceInspectionCode?: string | null;
  nr13Applicability?: Nr13Applicability;
  nr13Category?: string | null;
  insulation: string | null;
  status: 'ACTIVE' | 'INACTIVE' | 'OUT_OF_SERVICE';
}

export interface EquipmentDocument {
  id: string;
  clientId: string;
  equipmentId: string;
  title: string;
  documentType: string; // DRAWING, DATASHEET, CERTIFICATE, PID, ISOMETRIC, LINE_LIST, PROCEDURE, MANUAL, REPORT, OTHER
  filename: string;
  fileUrl?: string;
  uploadedAt: string;
  uploadedBy?: string;
  notes?: string;
}

export interface Equipment {
  id: EquipmentId;
  clientId: ClientId;
  tag: string;
  name: string;
  description: string | null;
  equipmentType: EquipmentType;
  areaId: AreaId | null;
  /** Ordem de Serviço de origem do cadastro (Bloco 04). Usa a OS oficial (WorkOrder), sem criar campo paralelo. */
  serviceOrderId?: WorkOrderId | null;
  projectId?: string | null; // Nível 2 da hierarquia obrigatória: CLIENTE -> PROJETO -> UNIDADE/ÁREA -> EQUIPAMENTO
  unitAreaId?: string | null; // Nível 3 da hierarquia obrigatória: UNIDADE/ÁREA do PROJETO
  system: string | null;
  pipeSystemApplicability?: 'APPLICABLE' | 'NOT_APPLICABLE' | undefined;
  pipingSystemId?: string | null; // Vinculado a Sistema de Tubulação (obrigatório para Tubulação)
  subsystem: string | null;
  service: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  yearOfManufacture: number | string | null;
  installationDate: string | null;
  commissioningDate: string | null;
  criticality: CriticalityLevel | null;
  location: string | null;
  notes: string | null;
  status: EquipmentStatus;

  // Tubulação (Piping Line) especificidades
  lineNumber?: string | null;
  fromEquipmentId?: EquipmentId | null;
  fromReference?: string | null;
  toEquipmentId?: EquipmentId | null;
  toReference?: string | null;

  // Fluido e Classificação
  fluid?: string | null;
  /** CÓDIGO DO FLUIDO — sigla própria (ex.: "GC"), distinta do texto de `fluid` (ex.: "GÁS COMBUSTÍVEL"). Nunca um substitui o outro. */
  fluidCode?: string | null;
  fluidClass?: string | null; // ex: Classe A, Classe B, Classe C, Classe D per NR-13

  // Dados de Projeto (Design)
  designPressure?: MeasurementValue | null;
  designTemperature?: MeasurementValue | null;
  pmtaValue?: number | string | null;
  pmtaUnit?: string | null;
  pmtaApplicability?: "required" | "optional" | "notApplicable";
  material?: string | null;
  nominalThickness?: MeasurementValue | null;
  corrosionAllowance?: MeasurementValue | null; // Sobre-espessura para corrosão
  fabricationStandard?: string | null; // Norma de Fabricação (ASME VIII, ASME B31.3, API 650) (Antigo manufacturingCode/projectStandard)
  projectCode?: string | null; // Código de Projeto
  /**
   * @deprecated Campo legado que combinava Desenho e Fluxograma num único
   * valor. Novas gravações usam `drawingReference`/`flowDiagramReference`
   * (campos oficiais separados). Mantido só para leitura de equipamentos
   * cadastrados antes da separação — não escrever aqui.
   */
  projectDrawingFlowchart?: string | null; // Desenho de Projeto / Fluxograma (legado)
  /** DESENHO (de Referência) — campo oficial separado de `flowDiagramReference`. */
  drawingReference?: string | null;
  /** FLUXOGRAMA (P&ID/PFD) — campo oficial separado de `drawingReference`. */
  flowDiagramReference?: string | null;

  // Tubulação - Projeto
  nps?: string | null; // NPS (ex: 2", 4", 6")
  schedule?: string | null; // Schedule (Sch 40, Sch 80, Sch STD)
  pipingDesignation?: string | null; // Denominação (STD, XS, XXS)
  outsideDiameter?: MeasurementValue | null; // Diâmetro Externo (OD)
  insulation?: string | null; // Isolamento
  fabricationCode?: string | null; // Código de Fabricação
  
  // Tubulação - Engenharia (Cadastro Técnico Inteligente)
  spec?: string | null;
  pressureClass?: string | null;
  materialFamily?: string | null;
  materialSpecification?: string | null;
  materialGrade?: string | null;
  dimensionalStandard?: string | null;
  calculatedInnerDiameter?: MeasurementValue | null;

  // Proteção / Revestimentos
  internalCoating?: string | null;
  externalCoating?: string | null;

  dimensionSource?: 'CATALOG' | 'MANUAL';
  dimensionManualSource?: string | null;
  dimensionManualJustification?: string | null;

  // Dados de Operação (Operating) - Estritamente separados de Projeto
  operatingPressure?: MeasurementValue | null;
  operatingTemperature?: MeasurementValue | null;
  operatingCondition?: string | null;

  // Integridade / Corrosão
  minimumRequiredThickness?: MinimumThicknessRecord | null;
  damageMechanisms?: EquipmentDamageMechanism[];

  // Inspeção em Serviço
  inServiceInspectionCode?: string | null; // Norma de Inspeção em Serviço (API 510, API 570, API 653)

  // Seção NR-13
  nr13Applicability?: Nr13Applicability;
  nr13Category?: string | null; // Categoria NR-13 (I, II, III, IV, V)
  
  // Type-specific data (Design, Operation, In-Service, Piping Lines, Documents) stored in a structured JSON payload
  payloadJson: string; 
  
  version: number;
  createdAt: string;
  createdBy?: string | null;
  updatedAt: string;
  updatedBy?: string | null;

  // Controlled Deletion / Inactivation audit metadata
  deletedAt?: string | null;
  deletedBy?: string | null;
  deletionReason?: string | null;
  inactivatedAt?: string | null;
  inactivatedBy?: string | null;
  inactivationReason?: string | null;
}

export interface EquipmentSnapshot {
  id: string;
  clientId: ClientId;
  equipmentId: EquipmentId;
  equipmentVersion: number;
  payloadJson: string; // The complete equipment data at the time of snapshot
  equipmentData?: Partial<Equipment>; // strongly-typed snapshot payload
  capturedAt: string;
  reason: string;
}

// ==========================================
// INSTRUMENTS DOMAIN MODEL
// ==========================================

export type InstrumentType =
  | 'PRESSURE_GAUGE'           // Manômetro
  | 'PRESSURE_TRANSMITTER'       // Transmissor de Pressão (PT/PIT)
  | 'TEMPERATURE_INDICATOR'     // Termômetro / Indicador de Temp (TI)
  | 'TEMPERATURE_TRANSMITTER'   // Transmissor de Temperatura (TT)
  | 'LEVEL_INDICATOR'           // Indicador / Visor de Nível (LI/LG)
  | 'LEVEL_TRANSMITTER'         // Transmissor de Nível (LT)
  | 'FLOW_METER'                // Medidor de Vazão (FI)
  | 'FLOW_TRANSMITTER'          // Transmissor de Vazão (FT)
  | 'SWITCH'                    // Pressostato / Termostato / Chave de Nível
  | 'THERMOCOUPLE'              // Termopar (TE)
  | 'RTD'                       // Termorresistência Pt100 (RTD)
  | 'DIFFERENTIAL_PRESSURE'     // Transmissor de Pressão Diferencial (PDT)
  | 'OTHER';

export type InstrumentStatus = 'ACTIVE' | 'INACTIVE' | 'OUT_OF_SERVICE' | 'CALIBRATION_DUE';

export interface InstrumentCalibration {
  id: InstrumentCalibrationId;
  clientId: ClientId;
  instrumentId: InstrumentId;
  date: string;
  certificate: string;
  result: 'APPROVED' | 'APPROVED_WITH_RESTRICTION' | 'REJECTED';
  responsible: string;
  calibrationEntity?: string | null;
  nextDueDate: string | null;
  documentId?: string | null;
  errorMaxFound?: string | null;
  uncertainty?: string | null;
  notes?: string | null;
  createdAt: string;
  createdBy?: string | null;
}

export interface Instrument {
  id: InstrumentId;
  clientId: ClientId;
  tag: string;
  name: string;
  instrumentType: InstrumentType;
  description: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  
  // Technical specifications
  rangeMin: number | string | null;
  rangeMax: number | string | null;
  unit: string | null;                    // bar, kgf/cm², °C, mm, m³/h, %, etc.
  accuracy: string | null;                // ±0.5%, Classe A, etc.
  processConnection: string | null;       // 1/2" NPT, Flange 2" 150#, etc.
  signalType: string | null;              // 4-20 mA, HART, Fieldbus, Modbus, 0-10V, 3-15 psi, Digital, N/A
  
  // Linkages
  projectId?: string | null;              // Projeto do Cliente (opcional; cadastros antigos não têm)
  equipmentId: EquipmentId | null;        // Vinculado a Equipamento
  pipeSystemApplicability?: 'APPLICABLE' | 'NOT_APPLICABLE' | undefined;
  pipingSystemId: string | null;          // Vinculado a Sistema de Tubulação
  pipingLineId: string | null;            // Vinculado a Linha específica
  areaId: AreaId | null;                  // Área / Setor Operacional
  system: string | null;
  subsystem: string | null;
  service: string | null;
  location: string | null;
  status: InstrumentStatus;
  notes: string | null;

  // Calibration summary
  calibrationRequired: boolean;
  lastCalibrationDate: string | null;
  nextCalibrationDate: string | null;
  certificateNumber: string | null;
  certificateDocumentId: string | null;
  calibrationStatus: 'VALID' | 'DUE' | 'EXPIRED' | 'NOT_REQUIRED' | null;

  version: number;
  createdAt: string;
  createdBy?: string | null;
  updatedAt: string;
  updatedBy?: string | null;

  // Controlled Deletion / Inactivation audit metadata
  deletedAt?: string | null;
  deletedBy?: string | null;
  deletionReason?: string | null;
  inactivatedAt?: string | null;
  inactivatedBy?: string | null;
  inactivationReason?: string | null;
}

// ==========================================
// SAFETY DEVICES DOMAIN MODEL (PSV / PRV)
// ==========================================

export type SafetyDeviceType =
  | 'PSV'                   // Válvula de Segurança e Alívio (PSV)
  | 'PRV'                   // Válvula Reguladora de Pressão (PRV)
  | 'RELIEF_VALVE'          // Válvula de Alívio
  | 'SAFETY_VALVE'          // Válvula de Segurança
  | 'RUPTURE_DISC'          // Disco de Ruptura (RD)
  | 'VACUUM_RELIEF_VALVE'   // Válvula Quebra-Vácuo
  | 'PRESSURE_VACUUM_VALVE' // Válvula de Pressão e Vácuo
  | 'BREATHER_VALVE'        // Válvula de Respiro para Tanques
  | 'OTHER';

export type SafetyDeviceStatus = 'ACTIVE' | 'INACTIVE' | 'OUT_OF_SERVICE' | 'TEST_DUE';

export interface SafetyDeviceTest {
  id: SafetyDeviceTestId;
  clientId: ClientId;
  safetyDeviceId: SafetyDeviceId;
  date: string;
  result: 'CONFORMING' | 'NON_CONFORMING' | 'ADJUSTED';
  setPressureFound: number | string | null;
  setPressureAdjusted: number | string | null;
  coldDifferentialTestPressure?: number | string | null; // CDTP
  seatTightnessTestPressure?: number | string | null;    // Estanqueidade
  bubbleCount?: number | string | null;                  // Bolhas/min API 527
  certificate: string;
  testBench?: string | null;
  responsibleProfessionalId?: string | null;
  responsibleName?: string | null;
  nextDueDate: string | null;
  documentId?: string | null;
  notes?: string | null;
  createdAt: string;
  createdBy?: string | null;
}

export interface SafetyDevice {
  id: SafetyDeviceId;
  clientId: ClientId;
  tag: string;
  name: string;
  deviceType: SafetyDeviceType;
  description: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;

  // Linkages (Protected asset)
  projectId?: string | null;                 // Projeto do Cliente (opcional; cadastros antigos não têm)
  protectedEquipmentId: EquipmentId | null;  // Equipamento protegido
  protectedSystemId: string | null;          // Sistema de tubulação protegido
  protectedLineId: string | null;            // Linha protegida
  
  // Technical Data
  setPressure: number | string | null;       // Pressão de Abertura / Ajuste (Set Pressure)
  setPressureUnit: string | null;            // kgf/cm², bar, psi, kPa
  backPressure: number | string | null;      // Contrapressão
  designPressure: number | string | null;    // Pressão de projeto
  overpressureAllowance: number | string | null; // Acúmulo / Sobrepressão (%)
  inletSize: string | null;                  // Ex: 2" 300#
  outletSize: string | null;                 // Ex: 3" 150#
  orificeDesignation: string | null;         // Ex: D, E, F, G, H, J, K, L, M, N, P, Q, R, T
  connectionType: string | null;             // Flangeada, Roscada, Soldada
  bodyMaterial: string | null;               // A216 WCB, A351 CF8M, etc.
  trimMaterial: string | null;               // Inox 316, Stellite, etc.
  springMaterial: string | null;
  service: string | null;
  fluid: string | null;
  areaId: AreaId | null;
  location: string | null;
  status: SafetyDeviceStatus;
  notes: string | null;

  // Test / Certification summary
  lastTestDate: string | null;
  nextTestDate: string | null;
  certificateNumber: string | null;
  testPressure: number | string | null;
  setPressureVerified: number | string | null;
  result: string | null;
  responsibleProfessionalId: string | null;
  certificateDocumentId: string | null;

  version: number;
  createdAt: string;
  createdBy?: string | null;
  updatedAt: string;
  updatedBy?: string | null;

  // Controlled Deletion / Inactivation audit metadata
  deletedAt?: string | null;
  deletedBy?: string | null;
  deletionReason?: string | null;
  inactivatedAt?: string | null;
  inactivatedBy?: string | null;
  inactivationReason?: string | null;
}

export interface TechnicalAssetDocument {
  id: string;
  clientId: ClientId;
  assetType: 'EQUIPMENT' | 'INSTRUMENT' | 'SAFETY_DEVICE';
  assetId: string;
  title: string;
  documentType: string; // DATASHEET, CERTIFICATE, DRAWING, MANUAL, SPECIFICATION, TEST_REPORT, OTHER
  filename: string;
  fileUrl?: string;
  uploadedAt: string;
  uploadedBy?: string;
  notes?: string;
}

// ==========================================
// REPOSITORIES INTERFACES
// ==========================================

export interface EquipmentRepository {
  saveEquipment(equipment: Equipment): Promise<void>;
  /** Bloco 04: suspende a gravação intermediária durante um lote de importação (implementação opcional). */
  beginBatch?(): void;
  /** Fecha o lote e grava uma única vez; propaga erro de armazenamento em vez de engolir (implementação opcional). */
  /** `commit=true` grava o lote; `commit=false` descarta (equivalente a ROLLBACK, onde suportado). */
  endBatch?(commit: boolean): Promise<void>;
  getEquipment(clientId: ClientId, id: EquipmentId): Promise<Equipment | null>;
  listEquipments(clientId: ClientId, options?: { projectId?: string | null }): Promise<Equipment[]>;
  deleteEquipment(clientId: ClientId, id: EquipmentId): Promise<void>;
  saveSnapshot(snapshot: EquipmentSnapshot): Promise<void>;
  getSnapshot(id: string): Promise<EquipmentSnapshot | null>;
  listSnapshotsByEquipment(clientId: ClientId, equipmentId: EquipmentId): Promise<EquipmentSnapshot[]>;
  deleteSnapshotsByEquipment(clientId: ClientId, equipmentId: EquipmentId): Promise<void>;
  saveDocument(doc: EquipmentDocument): Promise<void>;
  listDocuments(clientId: ClientId, equipmentId: EquipmentId): Promise<EquipmentDocument[]>;
  removeDocument(clientId: ClientId, documentId: string): Promise<void>;
}

export interface InstrumentRepository {
  saveInstrument(instrument: Instrument): Promise<void>;
  getInstrument(clientId: ClientId, id: InstrumentId): Promise<Instrument | null>;
  listInstruments(clientId: ClientId, filters?: { equipmentId?: string; areaId?: string; status?: string }): Promise<Instrument[]>;
  deleteInstrument(clientId: ClientId, id: InstrumentId): Promise<void>;
  
  saveCalibration(calibration: InstrumentCalibration): Promise<void>;
  listCalibrations(clientId: ClientId, instrumentId: InstrumentId): Promise<InstrumentCalibration[]>;
  deleteCalibration(clientId: ClientId, calibrationId: InstrumentCalibrationId): Promise<void>;
  
  saveDocument(doc: TechnicalAssetDocument): Promise<void>;
  listDocuments(clientId: ClientId, instrumentId: string): Promise<TechnicalAssetDocument[]>;
  removeDocument(clientId: ClientId, documentId: string): Promise<void>;
}

export interface SafetyDeviceRepository {
  saveSafetyDevice(device: SafetyDevice): Promise<void>;
  getSafetyDevice(clientId: ClientId, id: SafetyDeviceId): Promise<SafetyDevice | null>;
  listSafetyDevices(clientId: ClientId, filters?: { protectedEquipmentId?: string; areaId?: string; status?: string }): Promise<SafetyDevice[]>;
  deleteSafetyDevice(clientId: ClientId, id: SafetyDeviceId): Promise<void>;
  
  saveTest(test: SafetyDeviceTest): Promise<void>;
  listTests(clientId: ClientId, safetyDeviceId: SafetyDeviceId): Promise<SafetyDeviceTest[]>;
  deleteTest(clientId: ClientId, testId: SafetyDeviceTestId): Promise<void>;

  saveDocument(doc: TechnicalAssetDocument): Promise<void>;
  listDocuments(clientId: ClientId, safetyDeviceId: string): Promise<TechnicalAssetDocument[]>;
  removeDocument(clientId: ClientId, documentId: string): Promise<void>;
}

